import type { ReactNode } from 'react';
import { InlineLoader } from './loading';

/**
 * Content card that wraps a data table with shared loading and empty states.
 *
 * Extracted verbatim from DistributionListsPage and MailerSetupPage, where the
 * exact same markup appeared:
 *
 *   <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
 *     {loading ? (
 *       <div className="p-12 flex items-center justify-center text-gray-500 text-sm gap-2">
 *         <Loader2 className="w-4 h-4 animate-spin" /> Loading...
 *       </div>
 *     ) : <rows>.length === 0 ? (
 *       <div className="p-12 text-center text-gray-500 text-sm">{emptyMessage}</div>
 *     ) : (
 *       <table> ... </table>
 *     )}
 *   </div>
 *
 * The only differences between the two sites were:
 *   - the empty-state message string  -> `emptyMessage` prop
 *   - the `<table>` body               -> `children`
 * The `loading` and `isEmpty` flags stay computed at each call site and are
 * passed in, so every branch renders exactly the markup the pages had inline.
 * The loading line is drawn with the loading kit's ring (InlineLoader); the
 * block around it (p-12, centred) and the words are what they always were.
 */
interface TableCardWithStatesProps {
  /** True while data is being fetched; shows the centered spinner. */
  loading: boolean;
  /** True when there are no rows to display; shows `emptyMessage`. */
  isEmpty: boolean;
  /** Message rendered in the empty state (e.g. "No distribution lists yet."). */
  emptyMessage: string;
  /** The table (or any content) rendered once loaded and non-empty. */
  children: ReactNode;
}

export default function TableCardWithStates({
  loading,
  isEmpty,
  emptyMessage,
  children,
}: TableCardWithStatesProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {loading ? (
        <div className="p-12 flex items-center justify-center text-gray-500 text-sm gap-2">
          <InlineLoader label="Loading..." />
        </div>
      ) : isEmpty ? (
        <div className="p-12 text-center text-gray-500 text-sm">{emptyMessage}</div>
      ) : (
        children
      )}
    </div>
  );
}
